import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  AfterContentInit,
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { RouterLink } from '@angular/router';
import { NgLabelTemplateDirective, NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import KeenSlider, { KeenSliderInstance } from 'keen-slider';
import { BehaviorSubject, combineLatest, fromEvent, Observable, Subscription } from 'rxjs';
import { debounceTime, map, skip } from 'rxjs/operators';
import { PRODUCT_PLACEHOLDER } from 'src/app/app.settings';
import {
  Allergens,
  AllergensNames,
  discountLevels,
  InnerLineMap,
  LineMap,
  OnlineContentTypes,
  OnlineIconLine,
  OnlineImageClass,
  OnlineImageStyle,
  OnlineInfobox,
  OnlineStripContent,
  ProductComposition,
  ProductDetails,
  ProductLine,
  ProductModule,
  Saving,
} from 'src/app/models';
import { PickerType, SelectCount, SharedDetailContent } from 'src/app/models/product-detail';
import { buttonIcons } from 'src/app/modules/icon.module';
import { BasketService } from 'src/app/services/basket/basket.service';
import { ContentService } from 'src/app/services/content/content.service';
import { DialogService } from 'src/app/services/dialog/dialog.service';
import { ThemeService } from 'src/app/services/theme/theme.service';
import { IconButtonComponent, TextButtonComponent } from 'src/app/shared/components';
import { OnlineProductCardComponent } from 'src/app/shared/components/product/card';
import { CounterComponent } from 'src/app/shared/components/product/counter/counter.component';
import { PriceFieldComponent } from 'src/app/shared/components/product/price-field/price-field.component';
import { ContentSection } from 'src/app/shared/layout/content-section';
import { StripContainerComponent } from 'src/app/shared/layout/strips/strip-container.component';
import { environment } from 'src/environments/environment';
import { ContentImagePipe } from '../../../../shared/pipes/cms-image.pipe';
import { DisplayCurrencyPipe } from '../../../../shared/pipes/display-currency.pipe';
import { PriceSumPipe } from '../../../../shared/pipes/price-sum.pipe';
import { PrimaryProductImagePipe } from '../../../../shared/pipes/primary-product-image.pipe';
import { SanitizeHtmlPipe } from '../../../../shared/pipes/sanitize-html.pipe';
import { ProductPickerComponent } from '../nxt/composite/product-picker/product-picker.component';

@Component({
  selector: 'app-online-product-detail',
  templateUrl: './online-product-detail.component.html',
  styleUrls: ['../../../../../../node_modules/keen-slider/keen-slider.min.css', './online-product-detail.component.scss'],
  animations: [
    trigger('slide', [
      transition(':enter', [style({ opacity: 0, 'margin-right': '50px' }), animate('400ms', style({ opacity: 1, 'margin-right': '*' }))]),
      transition(':leave', [style({ opacity: 1 }), animate('200ms', style({ opacity: 0 }))]),
    ]),
    trigger('openClose', [
      state(
        'open',
        style({
          transform: 'translateY(0)',
          opacity: 1,
        }),
      ),
      state(
        'closed',
        style({
          transform: 'translateY(100%)',
          opacity: 0,
        }),
      ),
      transition('open => closed', [animate('500ms ease-out')]),
      transition('closed => open', [animate('500ms ease-out')]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    PrimaryProductImagePipe,
    ContentImagePipe,
    CommonModule,
    SanitizeHtmlPipe,
    PriceFieldComponent,
    RouterLink,
    NgSelectComponent,
    NgLabelTemplateDirective,
    NgOptionComponent,
    TextButtonComponent,
    CounterComponent,
    MatExpansionModule,
    ProductPickerComponent,
    StripContainerComponent,
    OnlineProductCardComponent,
    IconButtonComponent,
    DisplayCurrencyPipe,
    PriceSumPipe,
  ],
})
export class OnlineProductDetailComponent extends ContentSection implements OnChanges, AfterContentInit, AfterViewChecked, OnDestroy {
  private dialogService = inject(DialogService);
  private basketService = inject(BasketService);
  private themeService = inject(ThemeService);
  private elRef = inject(ElementRef);
  private ngZone = inject(NgZone);

  contentUrl = `${environment.cmsOrigin}`;
  contactUsUrl = `/${environment.urls.content.contact_us}`;

  private subscription: Subscription;
  private stickySubscription: Subscription;
  OnlineImageStyle = OnlineImageStyle;
  OnlineImageClass = OnlineImageClass;

  basketOverviewUrl = `/${environment.urls.basket}`;
  productUrl = `/${environment.urls.products}/`;
  deleteIcon = buttonIcons.bigCross;
  locale = environment.i18n.language;
  currency = environment.i18n.currency;

  @ViewChild('mobileImgSliderRef') imgSliderRef: ElementRef<HTMLElement>;
  // @ViewChild('smallProductAdditionSliderRef') smallProductAdditionRef: ElementRef<HTMLElement>;
  @ViewChildren('productAdditionSliderRef') productAdditionRefs: QueryList<ElementRef<HTMLElement>>;
  imgSlider: KeenSliderInstance;
  smallProductSlider: KeenSliderInstance;
  productSliders: KeenSliderInstance[];
  private viewChanged: boolean;

  @Input() product!: ProductDetails;
  @ViewChildren('mainModuleSelect') mainModuleRefs: QueryList<NgSelectComponent>;
  composition: ProductComposition;
  compositePrice: number;
  discountLevelPercent: number;
  validated: boolean;
  showAltPrice: boolean;
  requiredModules: Record<number, boolean>;
  hasSimpleModules: boolean;

  @Input() sharedContent: SharedDetailContent;
  @Input() basketProducts: Observable<any>;
  @Input() price: number;
  @Input() savings: Saving;
  @Output() reload = new EventEmitter<boolean>();

  basketProductLine: Observable<ProductLine>;
  lineId: BehaviorSubject<number>;
  placeholder = PRODUCT_PLACEHOLDER;
  simpleProduct: boolean;

  strips: OnlineStripContent[];
  iconLines: OnlineIconLine[];
  infoboxes: OnlineInfobox[];

  inStockLabel: string;
  notInStockLabel: string;

  @ViewChild('composite', { read: ElementRef, static: false }) compositeRef!: ElementRef;
  @ViewChild('controls', { read: ElementRef, static: false }) controlsRef!: ElementRef;
  @ViewChild('additions', { read: ElementRef, static: false }) additionsRef!: ElementRef;
  @ViewChild('stickyBottom', { read: ElementRef, static: false }) stickyRef!: ElementRef;
  sticky: boolean;
  totalOffset: number;
  calculateOffsetRef: any;
  toggleStickyRef: any;
  stickyOpen = false;

  constructor() {
    const contentService = inject(ContentService);
    super(contentService, 'productInfo');

    this.inStockLabel = this.content.get('inStockText') || 'På lager - forventet levering 1-3 hverdage';
    this.notInStockLabel = this.content.get('notInStockText') || 'Ikke på lager';

    this.subscription = new Subscription();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['product']) {
      this.hasSimpleModules = false;

      for (const module of this.product.productModules) {
        module.products.sort((a, b) => a.priority - b.priority);
        this.hasSimpleModules = this.hasSimpleModules || !module.main;
      }

      this.product.onlineProductLinks.sort((a, b) => a.displayPriority - b.displayPriority);

      this.composition = this.initComposition();
      this.validated = this.validate();
      this.lineId = new BehaviorSubject<number>(undefined);
      this.requiredModules = {};
      this.simpleProduct = this.product.productModules.length === 0;

      this.basketProductLine = combineLatest([this.basketProducts, this.lineId.pipe(skip(1))]).pipe(
        map(([products, lineId]: [LineMap, number]) => {
          const { id } = this.composition;

          this.composition.lineId = lineId;
          let product: ProductLine | InnerLineMap = products?.[id];

          if (!this.simpleProduct) {
            product = (product as InnerLineMap)?.[lineId];
          }

          return product as ProductLine;
        }),
      );

      const { id } = this.product;
      this.strips = this.contentService.getOnlineProductStripContent(id);
      this.iconLines = this.contentService.getOnlineProductContent(id, OnlineContentTypes.IconLine);
      this.infoboxes = this.contentService.getOnlineProductContent(id, OnlineContentTypes.Infobox);
      this.viewChanged = true;
    }
  }

  ngAfterContentInit() {
    this.themeService.subscribeComponent(this.elRef);
  }

  ngAfterViewChecked() {
    if (typeof document === 'undefined' || !this.viewChanged) return;
    this.viewChanged = false;
    if (this.imgSlider) this.imgSlider.destroy();
    if (this.smallProductSlider) this.smallProductSlider.destroy();
    if (this.productSliders?.length) this.productSliders.forEach((ps) => ps.destroy());

    if (this.product.imageUrls?.length > 0) {
      this.imgSlider = new KeenSlider(this.imgSliderRef.nativeElement, {
        loop: true,
        // mode: 'free-snap',
        slides: { origin: 'center', perView: 1.2, spacing: 12 },
      });
    }

    // this.smallProductSlider = new KeenSlider(this.smallProductAdditionRef.nativeElement, {
    //   loop: true,
    //   mode: 'free-snap',
    //   slides: { origin: 'center', perView: 1, spacing: 12 },
    // });

    this.productSliders = [];
    this.productAdditionRefs.forEach((par) => {
      this.productSliders.push(
        new KeenSlider(par.nativeElement, {
          loop: true,
          mode: 'free-snap',
          slides: { origin: 'center', perView: 'auto', spacing: 20 },
        }),
      );
    });
    this.imgSlider?.on('updated', () => window.dispatchEvent(new Event('resize')));

    // Sticky total
    this.stickySubscription?.unsubscribe();
    this.stickySubscription = new Subscription();
    this.calculateOffset();

    this.calculateOffsetRef = this.calculateOffset.bind(this);
    this.toggleStickyRef = this.toggleSticky.bind(this);
    this.stickySubscription.add(fromEvent(window, 'resize').pipe(debounceTime(1)).subscribe(this.calculateOffsetRef));
    this.stickySubscription.add(fromEvent(window, 'scroll').pipe().subscribe(this.toggleStickyRef));

    // TODO: use ResizeObserver instead to make the resize event immediately discovered

    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => window.dispatchEvent(new Event('resize')), 500);
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.stickySubscription?.unsubscribe();
    if (this.imgSlider) this.imgSlider.destroy();
    if (this.smallProductSlider) this.smallProductSlider.destroy();
    if (this.productSliders?.length) this.productSliders.forEach((ps) => ps.destroy());
  }

  initComposition(): ProductComposition {
    const composition = {
      ...this.product,
      moduleChoices: {},
    };

    this.compositePrice = this.price;
    this.discountLevelPercent = 0;
    this.showAltPrice = true;

    return composition;
  }

  add(module: ProductModule, productId: number, quantity: number, type?: PickerType) {
    new Promise<void>((resolve) => {
      this.showAltPrice = false;

      const product = module.products.find((product) => product.id === productId);
      if (!type) {
        type = module.maxPickCount === 1 ? 'pick-one' : 'pick-many';
      }

      // does module choice exist?
      if (!this.composition.moduleChoices[module.id]) {
        this.composition.moduleChoices[module.id] = { name: module.name, products: {} };
      }

      const moduleChoices = this.composition.moduleChoices[module.id];

      if (type === 'pick-one') {
        for (const p of Object.values(moduleChoices.products)) {
          this.compositePrice -= p.price * p.quantity;
        }
        moduleChoices.products = {};
      }

      // is this the first time the product is chosen?
      if (!moduleChoices.products[product.id]) {
        moduleChoices.products[product.id] = { ...product, quantity };
        this.compositePrice += product.price * quantity;
        return resolve();
      }

      if (type === 'pick-many' || type === 'pick-one') {
        return resolve();
      }

      const updatedQuantity = moduleChoices.products[product.id].quantity + quantity;
      moduleChoices.products[product.id] = { ...product, quantity: updatedQuantity };
      this.compositePrice += product.price * quantity;

      return resolve();
    }).finally(() => {
      if (this.composition.discountLevels) {
        let moduleQuantity = 0;
        Object.values(this.composition.moduleChoices).forEach((moduleChoice) => {
          Object.values(moduleChoice.products).forEach((product) => {
            moduleQuantity += product.quantity;
          });
        });

        this.discountLevelPercent = 0;
        this.composition.discountLevels.forEach((discountLevel: discountLevels) => {
          if (
            discountLevel.includeModuleProducts &&
            discountLevel.minimumQuantity <= moduleQuantity &&
            this.discountLevelPercent < discountLevel.discountPercent
          ) {
            this.discountLevelPercent = discountLevel.discountPercent;
          }
        });
      }

      this.validated = this.validate();

      if (!this.requiredModules[module.id]) {
        return;
      }

      let quantity = 0;
      const moduleChoices = this.composition.moduleChoices[module.id];
      for (const product of Object.values(moduleChoices.products)) {
        quantity += product.quantity;
      }

      if (module.minPickCount <= quantity && (!module.maxPickCount || quantity <= module.maxPickCount)) {
        delete this.requiredModules[module.id];
      }
    });
  }

  clearSelected(module: ProductModule) {
    const moduleChoices = this.composition.moduleChoices[module.id];
    for (const product of Object.values(moduleChoices.products)) {
      this.remove(module, product.id, 1);
    }
  }

  async remove(module: ProductModule, productId: number, quantity: number) {
    new Promise<void>((resolve) => {
      const moduleChoices = this.composition.moduleChoices[module.id];

      const product = module.products.find((product) => product.id === productId);

      // does module choice and product exist?
      if (!(moduleChoices && moduleChoices.products[product.id])) {
        return resolve();
      }

      // is there only one of the product?
      if (moduleChoices.products[product.id].quantity === quantity) {
        delete moduleChoices.products[product.id];
        this.compositePrice -= product.price * quantity;
        this.showAltPrice = Object.values(this.composition.moduleChoices).find((mc) => Object.keys(mc.products).length > 0) === undefined;
        return resolve();
      }

      const updatedQuantity = moduleChoices.products[product.id].quantity - quantity;
      moduleChoices.products[product.id] = { ...product, quantity: updatedQuantity };
      this.compositePrice -= product.price * quantity;
      return resolve();
    }).finally(() => {
      if (this.composition.discountLevels) {
        let moduleQuantity = 0;
        Object.values(this.composition.moduleChoices).forEach((moduleChoice) => {
          Object.values(moduleChoice.products).forEach((product) => {
            moduleQuantity += product.quantity;
          });
        });

        this.discountLevelPercent = 0;
        this.composition.discountLevels.forEach((discountLevel: discountLevels) => {
          if (
            discountLevel.includeModuleProducts &&
            discountLevel.minimumQuantity <= moduleQuantity &&
            this.discountLevelPercent < discountLevel.discountPercent
          ) {
            this.discountLevelPercent = discountLevel.discountPercent;
          }
        });
      }

      this.validated = this.validate();
    });
  }

  validate(): boolean {
    for (const module of this.product.productModules) {
      const { minPickCount, maxPickCount, singles } = module;
      const moduleChoices = this.composition.moduleChoices[module.id];

      if (!moduleChoices && minPickCount > 0) {
        return false;
      }

      if (moduleChoices) {
        let quantity = 0;
        for (const product of Object.values(moduleChoices.products)) {
          quantity += product.quantity;

          const { minCount, productMaxPickCount } = module.products.find((p) => p.id === product.id);
          if ((productMaxPickCount && product.quantity > productMaxPickCount) || product.quantity < minCount) {
            return false;
          }
        }

        if ((maxPickCount && quantity > maxPickCount) || quantity < minPickCount) {
          return false;
        }
      }
    }

    return true;
  }

  addToBasket(quantity: number) {
    quantity = quantity || 1;

    if (this.validated) {
      const line = this.basketService.addProduct(this.composition, quantity);
      this.lineId.next(line.lineId);
    }
  }

  removeFromBasket(prevQuantity: number, quantity: number) {
    quantity = quantity || 1;

    if (this.validated) {
      if (prevQuantity === quantity) {
        this.deleteFromBasket();
        return;
      }

      const line = this.basketService.removeProduct(this.composition, quantity);
      this.lineId.next(line?.lineId);
    }
  }

  deleteFromBasket() {
    this.basketService.deleteProduct(this.composition);
    this.buyMore();
  }

  validateRequiredAndScroll(mainElement: HTMLElement, pickerElement: HTMLElement) {
    const requiredModules: Record<number, boolean> = {};
    let mainRequired = false;

    for (const module of this.product.productModules) {
      const { minPickCount, maxPickCount, id, main } = module;
      if (minPickCount <= 0) {
        continue;
      }

      const moduleChoices = this.composition.moduleChoices[id];

      if (!moduleChoices) {
        requiredModules[id] = true;
        if (main) mainRequired = true;
        continue;
      }

      let quantity = 0;
      for (const product of Object.values(moduleChoices.products)) {
        quantity += product.quantity;

        const { minCount, productMaxPickCount } = module.products.find((p) => p.id === product.id);
        if ((productMaxPickCount && product.quantity > productMaxPickCount) || product.quantity < minCount) {
          requiredModules[id] = true;
          if (main) mainRequired = true;
        }
      }

      if (quantity < minPickCount || (maxPickCount && maxPickCount < quantity)) {
        requiredModules[id] = true;
        if (main) mainRequired = true;
      }
    }
    this.requiredModules = requiredModules;

    if (mainRequired) {
      window.scroll({ top: mainElement.getBoundingClientRect().top + window.scrollY, behavior: 'smooth' });
    } else {
      this.scroll(pickerElement);
    }
  }

  scroll(el: HTMLElement) {
    el.scrollIntoView({ behavior: 'smooth' });
  }

  calculateOffset() {
    const { offsetHeight } = this.compositeRef.nativeElement;
    const { clientHeight } = this.stickyRef.nativeElement;
    const top = this.compositeRef.nativeElement.getBoundingClientRect().top + window.scrollY;
    this.totalOffset = offsetHeight + top + (this.sticky ? 0 : clientHeight);

    this.compositeRef.nativeElement.style.setProperty('--sticky-height', `${clientHeight}px`);
    this.toggleSticky();
  }

  toggleSticky() {
    this.sticky = window.scrollY + window.innerHeight <= this.totalOffset;
    this.stickyOpen = this.hasSimpleModules || this.isElementAboveViewport(this.controlsRef.nativeElement);
  }

  isElementAboveViewport(el: HTMLElement) {
    const rect = el.getBoundingClientRect();
    return rect.bottom < 0;
  }

  buyMore() {
    this.lineId.next(undefined);
    this.mainModuleRefs.forEach((ref) => ref.handleClearClick());
    this.composition = this.resetComposition();
    this.validated = this.validate();
  }

  resetComposition(): ProductComposition {
    const composition = {
      ...this.product,
      moduleChoices: {},
    };

    const maxPickCount = this.composition.productModules[0]?.maxPickCount;

    this.showAltPrice = true;
    this.discountLevelPercent = 0;

    if (!maxPickCount || maxPickCount !== 1) {
      this.compositePrice = this.price;
      return composition;
    }

    composition.moduleChoices = this.composition.moduleChoices;

    return composition;
  }

  getSelectCount(): SelectCount {
    const selectCount: SelectCount = {
      minPickCount: 0,
      pickedCount: 0,
    };

    this.product.productModules.forEach((module) => {
      if (module.maxPickCount !== 1 && !module.singles && module.minPickCount > 0) {
        selectCount.minPickCount += module.minPickCount;
        if (this.composition.moduleChoices[module.id]?.products) {
          Object.values(this.composition.moduleChoices[module.id].products).forEach((product) => {
            selectCount.pickedCount += product.quantity;
          });
        }
      }
    });

    return selectCount;
  }

  getAllergenString(allergens: Allergens[]): string {
    const allergensNames = allergens.map((allergen) => AllergensNames[allergen]);
    return allergensNames.join(', ');
  }

  openCarousel(index: number) {
    const imageUrls = [this.product.primaryImageUrl, ...this.product.imageUrls];
    const data = { imageUrls, index };
    this.dialogService.openImageCarouselDialog(data, this.product.productDisplayName);
  }

  isProductLine(line: ProductLine | InnerLineMap): ProductLine {
    return line as ProductLine;
  }
}
